/**
 * RiskRegisterPanel — sortable, filterable risk register.
 *
 * Desktop: a table with click-to-sort headers and three filter dropdowns
 *          (site / status / category) plus a free-text search.
 * Mobile (< md): the same data renders as a card list — same sort + filter
 *          UI, just stacked for readability.
 *
 * Row click opens the existing RiskForm dialog (no slide-over migration in
 * G2 — that lives in G3's workspace polish). Add Risk asks the user to pick
 * a site first if there's more than one.
 *
 * Linear: MYD-213 (G2). Embedded by G3's RiskAssessmentWorkspace.
 */

import { useEffect, useMemo, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import {
	Plus,
	ShieldAlert,
	ArrowUp,
	ArrowDown,
	ChevronsUpDown,
	Search,
	Building2,
	X as CloseIcon,
} from 'lucide-react'
import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Badge } from 'components/ui/badge'
import { Input } from 'components/ui/input'
import { Select } from 'components/ui/select'
import { cn } from 'lib/utils'

import { fetchRisksComparison } from 'slices/riskSlice'
import RiskFormSlideOver from './RiskFormSlideOver'

const STATUS_OPTIONS = ['OPEN', 'MITIGATING', 'ACCEPTED', 'RESOLVED'] as const

const STATUS_CHIP: Record<string, string> = {
	OPEN: 'bg-red-100 text-red-700',
	MITIGATING: 'bg-amber-100 text-amber-700',
	ACCEPTED: 'bg-blue-100 text-blue-700',
	RESOLVED: 'bg-emerald-100 text-emerald-700',
}

type SortKey =
	| 'siteName'
	| 'category'
	| 'name'
	| 'score'
	| 'owner'
	| 'status'
	| 'mitigation'

type SortDir = 'asc' | 'desc'

interface RiskRow {
	_id: string
	site: string
	siteName: string
	name?: string
	category?: string
	description?: string
	likelihood: number
	impact: number
	mitigation?: string
	status?: string
	owner?: any
}

interface RiskRegisterPanelProps {
	evalId: string
	/**
	 * Optional. When provided, the panel delegates Add / Edit to the parent
	 * (typically RiskAssessmentWorkspace, which owns a single shared
	 * slide-over so HeatMap clicks and register-row clicks open the same
	 * form). When omitted, the panel mounts its own internal slide-over so
	 * standalone usage (e.g. embedded in a one-off page) keeps working.
	 */
	onAddRisk?: (siteId: string) => void
	onEditRisk?: (risk: RiskRow) => void
}

const RiskRegisterPanel = ({
	evalId,
	onAddRisk,
	onEditRisk,
}: RiskRegisterPanelProps) => {
	const dispatch = useDispatch()

	const comparison: any[] = useSelector(
		(state: any) => state.risks?.comparisonByEvaluation?.[evalId] || [],
	)

	const [sortKey, setSortKey] = useState<SortKey>('score')
	const [sortDir, setSortDir] = useState<SortDir>('desc')
	const [siteFilter, setSiteFilter] = useState<string>('')
	const [statusFilter, setStatusFilter] = useState<string>('')
	const [categoryFilter, setCategoryFilter] = useState<string>('')
	const [search, setSearch] = useState('')

	// Modal state
	const [formOpen, setFormOpen] = useState(false)
	const [formRisk, setFormRisk] = useState<RiskRow | null>(null)
	const [formSiteId, setFormSiteId] = useState<string>('')
	// "Pick a site" intermediate step before opening Add form (skipped if 0 or 1 site)
	const [sitePickerOpen, setSitePickerOpen] = useState(false)

	useEffect(() => {
		// @ts-ignore — thunk
		dispatch(fetchRisksComparison(evalId))
	}, [dispatch, evalId])

	const sites = useMemo(
		() =>
			comparison.map((s: any) => ({
				_id: String(s.siteId),
				name: s.siteName,
				riskCount: s.riskCount ?? 0,
			})),
		[comparison],
	)

	const allRisks: RiskRow[] = useMemo(() => {
		const flat: RiskRow[] = []
		for (const s of comparison) {
			for (const r of s?.risks ?? []) {
				flat.push({
					...r,
					site: String(s.siteId),
					siteName: s.siteName,
				})
			}
		}
		return flat
	}, [comparison])

	const categories = useMemo(() => {
		const set = new Set<string>()
		for (const r of allRisks) if (r.category) set.add(r.category)
		return Array.from(set).sort()
	}, [allRisks])

	const filteredRisks = useMemo(() => {
		let rows = allRisks
		if (siteFilter) rows = rows.filter((r) => r.site === siteFilter)
		if (statusFilter) rows = rows.filter((r) => (r.status || 'OPEN') === statusFilter)
		if (categoryFilter) rows = rows.filter((r) => r.category === categoryFilter)
		const q = search.trim().toLowerCase()
		if (q) {
			rows = rows.filter((r) => {
				return (
					(r.name && r.name.toLowerCase().includes(q)) ||
					(r.description && r.description.toLowerCase().includes(q)) ||
					(r.category && r.category.toLowerCase().includes(q)) ||
					(r.siteName && r.siteName.toLowerCase().includes(q))
				)
			})
		}
		return rows
	}, [allRisks, siteFilter, statusFilter, categoryFilter, search])

	const sortedRisks = useMemo(() => {
		const rows = filteredRisks.slice()
		const dirSign = sortDir === 'asc' ? 1 : -1
		rows.sort((a, b) => {
			let cmp = 0
			switch (sortKey) {
				case 'siteName':
					cmp = String(a.siteName ?? '').localeCompare(String(b.siteName ?? ''))
					break
				case 'category':
					cmp = String(a.category ?? '').localeCompare(String(b.category ?? ''))
					break
				case 'name':
					cmp = String(a.name ?? '').localeCompare(String(b.name ?? ''))
					break
				case 'score': {
					const aS = Number(a.likelihood) * Number(a.impact)
					const bS = Number(b.likelihood) * Number(b.impact)
					cmp = aS - bS
					break
				}
				case 'owner':
					cmp = String(a.owner?.name ?? a.owner?.email ?? '').localeCompare(
						String(b.owner?.name ?? b.owner?.email ?? ''),
					)
					break
				case 'status':
					cmp = String(a.status ?? 'OPEN').localeCompare(String(b.status ?? 'OPEN'))
					break
				case 'mitigation':
					cmp = (a.mitigation ? 1 : 0) - (b.mitigation ? 1 : 0)
					break
			}
			return cmp * dirSign
		})
		return rows
	}, [filteredRisks, sortKey, sortDir])

	const handleSort = useCallback(
		(key: SortKey) => {
			if (sortKey === key) {
				setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
			} else {
				setSortKey(key)
				setSortDir('asc')
			}
		},
		[sortKey],
	)

	const openEdit = useCallback(
		(r: RiskRow) => {
			if (onEditRisk) {
				onEditRisk(r)
				return
			}
			setFormRisk(r)
			setFormSiteId(r.site)
			setFormOpen(true)
		},
		[onEditRisk],
	)

	const openAdd = useCallback(() => {
		if (sites.length === 0) {
			toast.warning('Add at least one site to this evaluation before logging a risk.')
			return
		}
		if (sites.length === 1) {
			if (onAddRisk) {
				onAddRisk(sites[0]._id)
				return
			}
			setFormRisk(null)
			setFormSiteId(sites[0]._id)
			setFormOpen(true)
			return
		}
		setSitePickerOpen(true)
	}, [sites, onAddRisk])

	const handleSitePicked = useCallback(
		(siteId: string) => {
			setSitePickerOpen(false)
			if (onAddRisk) {
				onAddRisk(siteId)
				return
			}
			setFormRisk(null)
			setFormSiteId(siteId)
			setFormOpen(true)
		},
		[onAddRisk],
	)

	const handleFormClose = useCallback(() => {
		setFormOpen(false)
		setFormRisk(null)
		setFormSiteId('')
		// Refresh the comparison so newly created / edited risks land in the panel.
		// @ts-ignore — thunk
		dispatch(fetchRisksComparison(evalId))
	}, [dispatch, evalId])

	const clearAllFilters = () => {
		setSiteFilter('')
		setStatusFilter('')
		setCategoryFilter('')
		setSearch('')
	}

	const filtersActive =
		Boolean(siteFilter) ||
		Boolean(statusFilter) ||
		Boolean(categoryFilter) ||
		search.trim().length > 0

	return (
		<>
			<Card>
				<CardHeader className="pb-3 flex-row items-center justify-between gap-3 flex-wrap">
					<div>
						<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
							<ShieldAlert className="w-4 h-4" />
							Risk register ({sortedRisks.length}
							{filtersActive && allRisks.length !== sortedRisks.length
								? ` / ${allRisks.length}`
								: ''}
							)
						</h4>
						<p className="text-xs text-gray-500 mt-0.5">
							Click any row to edit. Filters narrow the list without affecting the heat map.
						</p>
					</div>
					<Button variant="primary" onClick={openAdd} className="gap-1.5">
						<Plus className="w-4 h-4" />
						Add risk
					</Button>
				</CardHeader>
				<CardContent>
					{/* Filter row */}
					<div className="flex flex-wrap items-center gap-2 mb-3">
						<div className="flex items-center gap-1.5 flex-1 min-w-[180px]">
							<Search className="w-3.5 h-3.5 text-gray-400" aria-hidden />
							<Input
								type="search"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Search name, description, category…"
								className="text-xs h-8"
								aria-label="Search risks"
							/>
						</div>
						{sites.length > 1 && (
							<Select
								value={siteFilter}
								onChange={(e) => setSiteFilter(e.target.value)}
								aria-label="Filter by site"
								className="text-xs h-8 w-auto"
							>
								<option value="">All sites</option>
								{sites.map((s) => (
									<option key={s._id} value={s._id}>
										{s.name}
									</option>
								))}
							</Select>
						)}
						<Select
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
							aria-label="Filter by status"
							className="text-xs h-8 w-auto"
						>
							<option value="">All statuses</option>
							{STATUS_OPTIONS.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</Select>
						{categories.length > 0 && (
							<Select
								value={categoryFilter}
								onChange={(e) => setCategoryFilter(e.target.value)}
								aria-label="Filter by category"
								className="text-xs h-8 w-auto"
							>
								<option value="">All categories</option>
								{categories.map((c) => (
									<option key={c} value={c}>
										{c}
									</option>
								))}
							</Select>
						)}
						{filtersActive && (
							<button
								type="button"
								onClick={clearAllFilters}
								className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
							>
								<CloseIcon className="w-3 h-3" />
								Clear filters
							</button>
						)}
					</div>

					{/* Empty state */}
					{allRisks.length === 0 ? (
						<div className="text-center py-10 text-sm text-gray-400">
							No risks logged yet. Click <strong>Add risk</strong> to start the register.
						</div>
					) : sortedRisks.length === 0 ? (
						<div className="text-center py-10 text-sm text-gray-400">
							No risks match the current filters.
						</div>
					) : (
						<>
							{/* Desktop table */}
							<div className="hidden md:block overflow-x-auto">
								<table className="w-full text-sm border-collapse">
									<thead>
										<tr className="border-b border-gray-200 text-left">
											<HeaderCell label="Site" k="siteName" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
											<HeaderCell label="Category" k="category" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
											<HeaderCell label="Risk" k="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
											<HeaderCell label="L × I" k="score" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="text-center" />
											<HeaderCell label="Owner" k="owner" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
											<HeaderCell label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
											<HeaderCell label="Mitigation" k="mitigation" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="text-center" />
										</tr>
									</thead>
									<tbody>
										{sortedRisks.map((r) => {
											const score = Number(r.likelihood) * Number(r.impact)
											const ownerName = r.owner?.name || r.owner?.email
											return (
												<tr
													key={r._id}
													onClick={() => openEdit(r)}
													className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
												>
													<td className="px-2 py-2 text-gray-700 truncate max-w-[120px]">
														{r.siteName}
													</td>
													<td className="px-2 py-2 text-gray-700 truncate max-w-[120px]">
														{r.category || '—'}
													</td>
													<td className="px-2 py-2 max-w-[280px]">
														<div className="font-medium text-gray-900 truncate">
															{r.name || 'Untitled risk'}
														</div>
														{r.description && (
															<div className="text-[11px] text-gray-500 truncate">
																{r.description}
															</div>
														)}
													</td>
													<td className="px-2 py-2 text-center text-gray-700 tabular-nums">
														{r.likelihood}×{r.impact}
														<span className="text-[10px] text-gray-400 ml-1">({score})</span>
													</td>
													<td className="px-2 py-2 text-gray-700 truncate max-w-[120px]">
														{ownerName || '—'}
													</td>
													<td className="px-2 py-2">
														<Badge
															className={cn(
																'text-[10px]',
																STATUS_CHIP[r.status || 'OPEN'],
															)}
														>
															{r.status || 'OPEN'}
														</Badge>
													</td>
													<td className="px-2 py-2 text-center text-xs">
														{r.mitigation ? (
															<span
																className="inline-block w-2 h-2 rounded-full bg-emerald-500"
																aria-label="Has mitigation plan"
																title="Has mitigation plan"
															/>
														) : (
															<span
																className="inline-block w-2 h-2 rounded-full bg-gray-200"
																aria-label="No mitigation plan"
																title="No mitigation plan"
															/>
														)}
													</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>

							{/* Mobile cards */}
							<div className="md:hidden space-y-2">
								{sortedRisks.map((r) => {
									const score = Number(r.likelihood) * Number(r.impact)
									const ownerName = r.owner?.name || r.owner?.email
									return (
										<button
											key={r._id}
											type="button"
											onClick={() => openEdit(r)}
											className="w-full text-left rounded-md border border-gray-200 px-3 py-2.5 hover:border-gray-300 hover:bg-gray-50"
										>
											<div className="flex items-start justify-between gap-2">
												<div className="flex-1 min-w-0">
													<div className="font-medium text-sm text-gray-900 truncate">
														{r.name || 'Untitled risk'}
													</div>
													<div className="text-[11px] text-gray-500 truncate flex items-center gap-1 mt-0.5">
														<Building2 className="w-3 h-3" />
														{r.siteName}
														{r.category && <span> · {r.category}</span>}
													</div>
												</div>
												<Badge
													className={cn(
														'text-[10px] shrink-0',
														STATUS_CHIP[r.status || 'OPEN'],
													)}
												>
													{r.status || 'OPEN'}
												</Badge>
											</div>
											<div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500">
												<span>L×I {r.likelihood}×{r.impact} ({score})</span>
												{ownerName && <span>· {ownerName}</span>}
												{r.mitigation && (
													<span className="inline-flex items-center gap-1 text-emerald-600">
														<span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
														Mitigation
													</span>
												)}
											</div>
										</button>
									)
								})}
							</div>
						</>
					)}
				</CardContent>
			</Card>

			{/* Site-picker prompt before Add when there are 2+ sites */}
			{sitePickerOpen && (
				<SitePickerOverlay
					sites={sites}
					onCancel={() => setSitePickerOpen(false)}
					onPick={handleSitePicked}
				/>
			)}

			{/* Internal slide-over — only mounted when no parent callbacks are
			    provided. RiskAssessmentWorkspace passes onAddRisk + onEditRisk
			    so it can own a single shared slide-over for both register-row
			    clicks and HeatMap-cell edit clicks. */}
			{!onAddRisk && !onEditRisk && (
				<RiskFormSlideOver
					open={formOpen}
					onClose={handleFormClose}
					evalId={evalId}
					siteId={formSiteId}
					risk={formRisk}
				/>
			)}
		</>
	)
}

interface HeaderCellProps {
	label: string
	k: SortKey
	sortKey: SortKey
	sortDir: SortDir
	onSort: (k: SortKey) => void
	className?: string
}

const HeaderCell = ({ label, k, sortKey, sortDir, onSort, className }: HeaderCellProps) => {
	const active = sortKey === k
	const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown
	return (
		<th
			className={cn(
				'py-2 px-2 text-xs font-semibold text-gray-600 select-none',
				className,
			)}
		>
			<button
				type="button"
				onClick={() => onSort(k)}
				className={cn(
					'inline-flex items-center gap-1 hover:text-gray-900',
					active && 'text-gray-900',
				)}
			>
				{label}
				<Icon className={cn('w-3 h-3', !active && 'opacity-40')} />
			</button>
		</th>
	)
}

interface SitePickerOverlayProps {
	sites: Array<{ _id: string; name: string; riskCount: number }>
	onCancel: () => void
	onPick: (siteId: string) => void
}

/**
 * Which site a new risk belongs to. Mounted only while open.
 *
 * On the standard Dialog since MYD-535 (it was a hand-rolled overlay): same
 * centring, backdrop, rounding and close button as every other popup, and
 * Escape now cancels too.
 */
const SitePickerOverlay = ({ sites, onCancel, onPick }: SitePickerOverlayProps) => {
	return (
		<Dialog open onClose={onCancel}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle className="text-base">Add risk: pick a site</DialogTitle>
					<DialogDescription>Risks are scoped to a site. Pick which site this risk applies to.</DialogDescription>
				</DialogHeader>
				<div className="space-y-1 max-h-[280px] overflow-y-auto p-4">
					{sites.map((s) => (
						<button
							key={s._id}
							type="button"
							onClick={() => onPick(s._id)}
							className="w-full text-left px-3 py-2 rounded-md border border-gray-200 hover:border-[#0080fe] hover:bg-[#0080fe]/5 transition-colors text-sm flex items-center justify-between"
						>
							<span className="font-medium text-gray-900 truncate">{s.name}</span>
							<span className="text-xs text-gray-500 shrink-0">
								{s.riskCount} risk{s.riskCount === 1 ? '' : 's'}
							</span>
						</button>
					))}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default RiskRegisterPanel
